import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, TrendingDown, Play, Pause, CheckCircle, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getUserExperiments } from '../../../services/api';

const ExperimentsBlock = ({ user, size = 'medium', refreshTrigger }) => {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExperiments();
  }, [user, refreshTrigger]);

  const loadExperiments = async () => {
    if (!user?.id) return;
    
    try {
      // Get experiments from all user's prompts
      const experimentsData = await getUserExperiments(user.id);
      
      // Ensure we always have an array, handle different response structures
      let experimentsArray = [];
      if (Array.isArray(experimentsData)) {
        experimentsArray = experimentsData;
      } else if (experimentsData && Array.isArray(experimentsData.data)) {
        experimentsArray = experimentsData.data;
      } else if (experimentsData && experimentsData.success && Array.isArray(experimentsData.data)) {
        experimentsArray = experimentsData.data;
      }
      
      setExperiments(experimentsArray);
    } catch (error) {
      console.error('Failed to load experiments:', error);
      setExperiments([]); // Ensure we always set an array on error
    } finally {
      setLoading(false);
    }
  };

  const getDisplayCount = () => {
    switch (size) {
      case 'small':
        return 2;
      case 'medium':
        return 3;
      case 'large':
        return 6;
      default:
        return 3;
    }
  };

  // Safely slice the experiments array
  const displayExperiments = Array.isArray(experiments) ? experiments.slice(0, getDisplayCount()) : [];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return CheckCircle;
      case 'running':
        return Play;
      case 'paused':
        return Pause;
      default:
        return Zap;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-celadon-600 dark:text-celadon-400';
      case 'running':
        return 'text-celadon-600 dark:text-celadon-400';
      case 'paused':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getImprovementIcon = (improvement) => {
    if (!improvement) return null;
    return improvement > 0 ? TrendingUp : TrendingDown;
  };

  const getImprovementColor = (improvement) => {
    if (!improvement) return 'text-gray-600 dark:text-gray-400';
    return improvement > 0 ? 'text-celadon-600 dark:text-celadon-400' : 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (experiments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <Zap className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-3" />
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">No experiments yet</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Start experimenting with different prompt versions to optimize performance
        </p>
        <Link
          to="/experiments"
          className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-celadon-600 hover:bg-celadon-700 dark:bg-celadon-500 dark:hover:bg-celadon-600 rounded-md transition-colors"
        >
          <Zap className="h-3 w-3 mr-1" />
          Create Experiment
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Experiments</span>
        </div>
        <Link 
          to="/experiments"
          className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300"
        >
          View all
        </Link>
      </div>

      <div className="space-y-3 overflow-y-auto max-h-full">
        {displayExperiments.map((experiment) => {
          const StatusIcon = getStatusIcon(experiment.status);
          const ImprovementIcon = getImprovementIcon(experiment.improvement);
          
          return (
            <div
              key={experiment.id}
              className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusIcon className={`h-3 w-3 ${getStatusColor(experiment.status)}`} />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {experiment.name}
                    </h4>
                  </div>
                  
                  {size !== 'small' && experiment.prompt_name && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {experiment.prompt_name}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        experiment.status === 'completed' ? 'bg-celadon-100 text-celadon-800 dark:bg-celadon-900/30 dark:text-celadon-300' :
                        experiment.status === 'running' ? 'bg-celadon-100 text-celadon-800 dark:bg-celadon-900/30 dark:text-celadon-300' :
                        experiment.status === 'paused' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {experiment.status}
                      </span>
                      
                      {experiment.improvement && (
                        <div className={`flex items-center gap-1 ${getImprovementColor(experiment.improvement)}`}>
                          {ImprovementIcon && <ImprovementIcon className="h-3 w-3" />}
                          <span className="text-xs font-medium">
                            {experiment.improvement > 0 ? '+' : ''}{experiment.improvement}%
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <Link
                      to={`/experiments/${experiment.id}`}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      title="View experiment"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>

                  {size !== 'small' && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Started {formatDistanceToNow(new Date(experiment.created_at), { addSuffix: true })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExperimentsBlock; 